import { useCallback, useRef, useState } from 'react';
import gp from '@sagold/json-pointer';
import {
  ArrayNode,
  DefaultNodeOptions,
  Editor,
  isJsonError,
  JsonSchema,
  Widget,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';
import classnames from 'clsx';

import { Button } from '../../../components/buttons/Button';
import { FormField } from '../../../components/formfield/FormField';
import { GoogleFontIcon } from '../../../components/icons/GoogleFontIcon';
import { Modal } from '../../../components/modal/Modal';
import { SectionHeader } from '../../../components/sectionheader/SectionHeader';
import { SingleSelect } from '../../../components/selects/SingleSelect';

import { ArrayItem, ArrayItemOptions } from './ArrayItem';
import { ArrayWidgetActions } from './ArrayWidgetActions';

export type ArrayOptions = DefaultNodeOptions<{
  /** if set, will add an accordion in the given toggle state */
  collapsed?: boolean;
  /** Is set internally to true to add a delete option for this object. */
  isOptional?: boolean;
  /** set to true to show inline button at the end of the array to add another item */
  inlineAddItemOption?: boolean;
  /** if true will add a separator line to the header */
  headerSeparator?: boolean;
  /** header font size relative to 1 (em). Defaults to 1 */
  headerFontSize?: number;
  /** set to false to deactivate any array-controls */
  controls?: boolean;
  /** set to false to deactivate array-header-controls */
  rowControls?: boolean;
  /** set to false to deactivate array-row-controls */
  headerControls?: boolean;
  /** set to true to inline description */
  descriptionInline?: boolean;
  /** set to true to see rows like table-grid */
  showGrid?: boolean;
  /** set to true to show a header calculating the sum in the grid,
   * requires showGrid to be true & gridHeaderSum plugin
   * */
  showGridSumHeader?: boolean;
  /** a relative path for girSumObject */
  gridSumJsonPath?: string;
}>;

function getActionStates(node: ArrayNode) {
  const minItems = node.schema.minItems || 0;
  let isAddEnabled =
    node.schema.maxItems == null
      ? true
      : node.children.length < node.schema.maxItems;
  if (
    Array.isArray(node.schema.items) &&
    (node.schema.additionalItems === false ||
      node.schema.additionalItems == null)
  ) {
    isAddEnabled = node.children.length < node.schema.items.length;
  }
  return { isAddEnabled, isDeleteEnabled: minItems < node.children.length };
}

function getGridHeaderTitle(key: string, value: JsonSchema, editor: Editor) {
  let title: string = key;
  if (value.title) {
    title = value.title;
  } else if (value.$ref) {
    const refSchema = gp.get(editor.getSchema(), value.$ref) as JsonSchema;
    if (refSchema.title) {
      title = refSchema.title;
    }
  }
  return title;
}

export const ArrayWidget = widget<ArrayNode<ArrayOptions>>(
  ({ editor, node, options }) => {
    const portalContainer = useRef<HTMLDivElement>(null);
    const [showContent, setShowContent] = useState<boolean>(
      options.collapsed != null ? !options.collapsed : true
    );
    const [showSelection, setShowSelection] = useState<boolean>(false);
    const closeSelection = () => setShowSelection(false);
    const showHeaderControls =
      options.controls ?? options.headerControls !== false;
    const showRowControls = options.controls ?? options.rowControls !== false;
    const childOptions: ArrayItemOptions = {
      disabled: options.disabled,
      readOnly: options.readOnly,
    };
    const ref = useRef<HTMLDivElement>(null);
    const { isAddEnabled, isDeleteEnabled } = getActionStates(node);
    const { description, title, collapsed } = options;
    const showHeader = title || description || collapsed != null;
    const insertOptions = editor.getArrayAddOptions(node);
    const insertItem = useCallback(() => {
      editor.appendItem(node, insertOptions[0]);
      setShowContent(true);
    }, [node, editor, insertOptions]);

    const showGrid =
      options.showGrid && // showGrid is set true on options
      insertOptions.length === 1 && // grid show not be shown on multi option array
      insertOptions[0].properties; // the array should have a object to show grid

    const addItemButton =
      insertOptions.length > 1 ? (
        <Button
          key="add"
          variant="text"
          disabled={!isAddEnabled || options.disabled}
          icon={<GoogleFontIcon name="add" />}
          // {...insertModalTriggerProps}
        >
          add item
        </Button>
      ) : (
        <Button
          key="add"
          disabled={!isAddEnabled || options.disabled}
          variant="text"
          icon={<GoogleFontIcon name="add" />}
          onClick={insertItem}
        >
          add item
        </Button>
      );

    return (
      <FormField
        widgetType="array"
        node={node}
        options={options}
        showError={false}
        showDescription={false}
        className={classnames(options.classNames)}
        ref={portalContainer}
      >
        {showHeader && (
          <FormField.Header>
            <SectionHeader>
              {collapsed != null && (
                <Button
                  variant="text"
                  className={classnames(
                    'rje-widget-action',
                    showContent
                      ? 'rje-widget-action--uncollapsed'
                      : 'rje-widget-action--collapsed'
                  )}
                  onClick={() => setShowContent(!showContent)}
                  icon={
                    <GoogleFontIcon
                      name={showContent ? 'expand_more' : 'expand_less'}
                    />
                  }
                />
              )}
              <SectionHeader.Label
                title={title}
                size={options.headerFontSize}
                separator={options.headerSeparator ?? true}
                description={
                  options.descriptionInline === true ? undefined : description
                }
              />
              {showHeaderControls && (
                <ArrayWidgetActions
                  editor={editor}
                  node={node}
                  options={options}
                  actions={options.readOnly ? [] : [addItemButton]}
                />
              )}
            </SectionHeader>
            <FormField.Description enabled={options.descriptionInline === true}>
              {description}
            </FormField.Description>
            <FormField.Error errors={node.errors} />
          </FormField.Header>
        )}

        <div className="rje-array__grid-header">
          {showContent &&
            showGrid &&
            Object.entries(insertOptions[0].properties).map(
              ([key, value]: [string, any]) => {
                const title = getGridHeaderTitle(key, value, editor);

                let sumNode;
                if (
                  options?.showGridSumHeader &&
                  typeof options?.gridSumJsonPath === 'string'
                ) {
                  const editorSumNode = editor.getNode(
                    `${options.gridSumJsonPath}/${key}`
                  );
                  if (!isJsonError(editorSumNode)) {
                    sumNode = editorSumNode;
                  }
                }
                return (
                  <div key={key}>
                    <div>{title}</div>
                    {sumNode && (
                      <Widget
                        editor={editor}
                        node={sumNode}
                        options={{
                          readOnly: true,
                        }}
                      />
                    )}
                  </div>
                );
              }
            )}
        </div>
        <div className="rje-array__items" ref={ref}>
          {showContent &&
            node.children.map((child) => (
              <ArrayItem
                disabled={options.disabled || options.readOnly}
                editor={editor}
                key={child.id}
                node={child}
                controls={showRowControls && options.readOnly !== true}
                size={node.children.length}
                options={childOptions}
                optional={isDeleteEnabled}
              />
            ))}
        </div>
        {showContent &&
          options.inlineAddItemOption !== false &&
          options.readOnly !== true && (
            <div
              className={`rje-array__actions ${
                node.children.length % 2 ? 'rje--even' : 'rje--odd'
              }`}
            >
              {insertOptions.length > 1 ? (
                <Button
                  variant="text"
                  disabled={!isAddEnabled || options.disabled}
                  icon={<GoogleFontIcon name="add" />}
                  onClick={() => setShowSelection(true)}
                />
              ) : (
                <Button
                  variant="text"
                  disabled={!isAddEnabled || options.disabled}
                  icon={<GoogleFontIcon name="add" />}
                  onClick={insertItem}
                />
              )}
            </div>
          )}
        {/* TODO: adjust implementation to use modalType="action" once it is in use */}
        <Modal modalType="info" opened={showSelection} onClose={closeSelection}>
          <ModalContentSelectItem
            editor={editor}
            node={node}
            close={closeSelection}
            items={insertOptions}
            onInsertItem={() => setShowContent(true)}
          />
        </Modal>
      </FormField>
    );
  }
);

export type ModalContentSelectItemProps = {
  close: () => void;
  editor: Editor;
  node: ArrayNode;
  items: JsonSchema[];
  onInsertItem?: () => void;
};

export function ModalContentSelectItem({
  close,
  editor,
  node,
  items,
  onInsertItem,
}: ModalContentSelectItemProps) {
  const [selected, setSelected] = useState<string | number>(0);
  return (
    <>
      <SingleSelect
        defaultValue={`${selected}`}
        value={`${selected}`}
        onChange={(value) => setSelected(`${value}`)}
        data={items.map((o, index) => ({
          label: `${o.title ?? index}`,
          value: `${index}`,
        }))}
      />
      <div className="rje-modal__footer">
        <Button variant="text" onClick={close}>
          cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            editor.appendItem(node, items[selected as number]);
            onInsertItem && onInsertItem();
            close();
          }}
        >
          insert item
        </Button>
      </div>
    </>
  );
}

export const ArrayWidgetPlugin: WidgetPlugin<ArrayNode> = {
  id: 'array-widget',
  use: (node) => node.schema.type === 'array',
  Widget: ArrayWidget,
};
